'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HeatIndexCalculator() {
  const t = useTranslations("tools.heatIndexCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [mode, setMode] = useState<'humidity' | 'dewpoint'>('humidity');
  const [humidityInputs, setHumidityInputs] = useState({
    temperature: 85,
    temperatureUnit: 'fahrenheit' as 'fahrenheit' | 'celsius' | 'kelvin',
    humidity: 70,
  });
  const [dewpointInputs, setDewpointInputs] = useState({
    temperature: 85,
    temperatureUnit: 'fahrenheit' as 'fahrenheit' | 'celsius' | 'kelvin',
    dewpoint: 70,
    dewpointUnit: 'fahrenheit' as 'fahrenheit' | 'celsius' | 'kelvin',
  });
  const [results, setResults] = useState<{
    heatIndex: number;
    unit: string;
    warningLevel: {
      level: string;
      title: string;
      description: string;
    };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Temperature conversion functions
  const convertToFahrenheit = (temp: number, unit: 'fahrenheit' | 'celsius' | 'kelvin'): number => {
    if (unit === 'celsius') {
      return (temp * 9/5) + 32;
    } else if (unit === 'kelvin') {
      return (temp - 273.15) * 9/5 + 32;
    }
    return temp;
  };

  const convertFromFahrenheit = (tempF: number, unit: 'fahrenheit' | 'celsius' | 'kelvin'): number => {
    if (unit === 'celsius') {
      return (tempF - 32) * 5/9;
    } else if (unit === 'kelvin') {
      return (tempF - 32) * 5/9 + 273.15;
    }
    return tempF;
  };

  const calculateRelativeHumidity = (tempF: number, dewPointF: number): number => {
    const tempC = (tempF - 32) * 5/9;
    const dewPointC = (dewPointF - 32) * 5/9;

    const a = 17.27;
    const b = 237.7;

    const alpha = ((a * tempC) / (b + tempC)) + Math.log(1);
    const beta = ((a * dewPointC) / (b + dewPointC)) + Math.log(1);

    const rh = 100 * Math.exp(beta - alpha);
    return Math.min(100, Math.max(0, rh));
  };

  const calculateHeatIndex = (tempF: number, rh: number): number => {
    if (tempF < 80) {
      return tempF;
    }

    const T = tempF;
    const R = rh;

    let HI = -42.379 +
             2.04901523 * T +
             10.14333127 * R -
             0.22475541 * T * R -
             6.83783e-3 * T * T -
             5.481717e-2 * R * R +
             1.22874e-3 * T * T * R +
             8.5282e-4 * T * R * R -
             1.99e-6 * T * T * R * R;

    if (R < 13 && T >= 80 && T <= 112) {
      const adjustment = ((13 - R) / 4) * Math.sqrt((17 - Math.abs(T - 95)) / 17);
      HI -= adjustment;
    } else if (R > 85 && T >= 80 && T <= 87) {
      const adjustment = ((R - 85) / 10) * ((87 - T) / 5);
      HI += adjustment;
    }

    if (HI < T) {
      HI = 0.5 * (T + 61.0 + ((T - 68.0) * 1.2) + (R * 0.094));
    }

    return HI;
  };

  const getWarningLevel = (heatIndexF: number) => {
    if (heatIndexF >= 130) {
      return {
        level: 'extreme-danger',
        title: t('warningExtremeDanger'),
        description: t('warningExtremeDangerDesc'),
      };
    } else if (heatIndexF >= 105) {
      return {
        level: 'danger',
        title: t('warningDanger'),
        description: t('warningDangerDesc'),
      };
    } else if (heatIndexF >= 90) {
      return {
        level: 'extreme-caution',
        title: t('warningExtremeCaution'),
        description: t('warningExtremeCautionDesc'),
      };
    } else if (heatIndexF >= 80) {
      return {
        level: 'caution',
        title: t('warningCaution'),
        description: t('warningCautionDesc'),
      };
    } else {
      return {
        level: 'normal',
        title: t('warningNormal'),
        description: t('warningNormalDesc'),
      };
    }
  };

  const handleHumidityCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const { temperature, temperatureUnit, humidity } = humidityInputs;

    if (humidity < 0 || humidity > 100) {
      setError(t('errorHumidityRange'));
      setResults(null);
      return;
    }

    const tempF = convertToFahrenheit(temperature, temperatureUnit);
    const heatIndexF = calculateHeatIndex(tempF, humidity);
    const heatIndexConverted = convertFromFahrenheit(heatIndexF, temperatureUnit);
    const unitSymbol = temperatureUnit === 'fahrenheit' ? '°F' : (temperatureUnit === 'celsius' ? '°C' : 'K');

    setResults({
      heatIndex: heatIndexConverted,
      unit: unitSymbol,
      warningLevel: getWarningLevel(heatIndexF),
    });
  };

  const handleDewpointCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const { temperature, temperatureUnit, dewpoint, dewpointUnit } = dewpointInputs;

    const tempF = convertToFahrenheit(temperature, temperatureUnit);
    const dewpointF = convertToFahrenheit(dewpoint, dewpointUnit);

    if (dewpointF > tempF) {
      setError(t('errorDewpointHigher'));
      setResults(null);
      return;
    }

    const rh = calculateRelativeHumidity(tempF, dewpointF);
    const heatIndexF = calculateHeatIndex(tempF, rh);
    const heatIndexConverted = convertFromFahrenheit(heatIndexF, temperatureUnit);
    const unitSymbol = temperatureUnit === 'fahrenheit' ? '°F' : (temperatureUnit === 'celsius' ? '°C' : 'K');

    setResults({
      heatIndex: heatIndexConverted,
      unit: unitSymbol,
      warningLevel: getWarningLevel(heatIndexF),
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    orange: '#ea580c',
    orangeLight: '#fff7ed',
    orangeBorder: '#fed7aa',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
      marginBottom: '0',
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(234, 88, 12, 0.08)',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
    },
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    toolTitle: {
      color: colors.orange,
      fontWeight: 700,
      marginBottom: '0.5rem',
    },
    toolSubtitle: {
      color: '#9a3412',
      margin: 0,
    },
    modeSelector: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '2rem',
      flexWrap: 'wrap' as const,
    },
    modeButton: {
      flex: 1,
      minWidth: '200px',
      padding: '0.875rem 1.5rem',
      border: '2px solid',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontWeight: 600,
      fontSize: '1rem',
    },
    modeButtonActive: {
      borderColor: colors.orange,
      background: colors.orange,
      color: 'white',
    },
    modeButtonInactive: {
      borderColor: colors.orangeBorder,
      color: colors.orange,
    },
    formCard: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(234, 88, 12, 0.1)',
      marginBottom: '2rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      color: '#9a3412',
      fontWeight: 600,
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    inputWrapper: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
    },
    input: {
      flex: 1,
      minWidth: '150px',
      padding: '0.75rem',
      border: '2px solid #fed7aa',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: '#fffbf5',
      transition: 'all 0.3s ease',
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #fed7aa',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '140px',
    },
    calculateButton: {
      width: '100%',
      padding: '0.875rem',
      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(234, 88, 12, 0.2)',
    },
    resultsCard: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(234, 88, 12, 0.1)',
      marginTop: '2rem',
    },
    resultsTitle: {
      color: colors.orange,
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
      fontWeight: 600,
    },
    heatIndexDisplay: {
      background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
      borderRadius: '0.75rem',
      padding: '2rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
      border: '3px solid #fed7aa',
    },
    heatIndexValue: {
      fontSize: '3rem',
      fontWeight: 700,
      color: colors.orange,
      marginBottom: '0.5rem',
    },
    heatIndexLabel: {
      color: '#9a3412',
      fontSize: '1rem',
      fontWeight: 600,
    },
    warningLevel: {
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid',
    },
    warningTitle: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    warningDesc: {
      margin: 0,
      lineHeight: 1.6,
    },
    additionalInfo: {
      background: '#f0fdfa',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      borderLeft: '4px solid #14b8a6',
    },
    additionalInfoTitle: {
      color: '#0f766e',
      margin: '0 0 0.75rem 0',
      fontSize: '1.1rem',
      fontWeight: 600,
    },
    additionalInfoList: {
      margin: 0,
      paddingLeft: '1.5rem',
      color: '#115e59',
    },
    additionalInfoItem: {
      marginBottom: '0.5rem',
      lineHeight: 1.6,
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      paddingLeft: '1.5rem',
      color: colors.textSecondary,
      lineHeight: 1.8,
    },
    listItem: {
      marginBottom: '0.75rem',
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600,
    },
    insightCard: {
      background: 'linear-gradient(135deg, rgba(255, 237, 213, 0.3) 0%, rgba(254, 215, 170, 0.3) 100%)',
      padding: '1rem',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      borderLeft: '4px solid #f97316',
    },
    faqItem: {
      marginBottom: '1.5rem',
      paddingBottom: '1.5rem',
      borderBottom: '1px solid rgba(255, 200, 180, 0.3)',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
    },
    faqAnswer: {
      color: colors.textSecondary,
      lineHeight: 1.7,
      margin: 0,
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
      transition: 'color 0.3s ease',
    },
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolTitleStyle = getResponsiveStyle(
    baseStyles.toolTitle,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formCardStyle = getResponsiveStyle(
    baseStyles.formCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    baseStyles.resultsCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const heatIndexValueStyle = getResponsiveStyle(
    baseStyles.heatIndexValue,
    { fontSize: '2.5rem' },
    { fontSize: '3rem' },
    { fontSize: '3rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const getWarningLevelStyle = (level: string) => {
    const baseWarningStyle = { ...baseStyles.warningLevel };

    if (level === 'caution' || level === 'normal') {
      return {
        ...baseWarningStyle,
        background: '#fef3c7',
        borderColor: '#f59e0b',
        color: '#92400e',
      };
    } else if (level === 'extreme-caution') {
      return {
        ...baseWarningStyle,
        background: '#fed7aa',
        borderColor: '#f97316',
        color: '#9a3412',
      };
    } else if (level === 'danger') {
      return {
        ...baseWarningStyle,
        background: '#fecaca',
        borderColor: '#ef4444',
        color: '#991b1b',
      };
    } else if (level === 'extreme-danger') {
      return {
        ...baseWarningStyle,
        background: '#fecdd3',
        borderColor: '#dc2626',
        color: '#7f1d1d',
      };
    }
    return baseWarningStyle;
  };

  return (
    <div style={containerStyle}>
      <header style={baseStyles.header}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={baseStyles.toolHeader}>
            <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
            <p style={baseStyles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <div style={baseStyles.modeSelector}>
            <button
              style={{
                ...baseStyles.modeButton,
                ...(mode === 'humidity' ? baseStyles.modeButtonActive : baseStyles.modeButtonInactive),
              }}
              onClick={() => setMode('humidity')}
            >
              {t('modeHumidity')}
            </button>
            <button
              style={{
                ...baseStyles.modeButton,
                ...(mode === 'dewpoint' ? baseStyles.modeButtonActive : baseStyles.modeButtonInactive),
              }}
              onClick={() => setMode('dewpoint')}
            >
              {t('modeDewpoint')}
            </button>
          </div>

          {mode === 'humidity' ? (
            <form onSubmit={handleHumidityCalculate} style={formCardStyle}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('airTemperature')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.1"
                    value={humidityInputs.temperature}
                    onChange={(e) => setHumidityInputs({...humidityInputs, temperature: parseFloat(e.target.value)})}
                    style={baseStyles.input}
                    required
                  />
                  <select
                    value={humidityInputs.temperatureUnit}
                    onChange={(e) => setHumidityInputs({...humidityInputs, temperatureUnit: e.target.value as any})}
                    style={baseStyles.select}
                  >
                    <option value="fahrenheit">°F</option>
                    <option value="celsius">°C</option>
                    <option value="kelvin">K</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('relativeHumidity')}</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={humidityInputs.humidity}
                  onChange={(e) => setHumidityInputs({...humidityInputs, humidity: parseFloat(e.target.value)})}
                  style={baseStyles.input}
                  required
                />
              </div>

              <button type="submit" style={baseStyles.calculateButton}>
                {t('calculateButton')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleDewpointCalculate} style={formCardStyle}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('airTemperature')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.1"
                    value={dewpointInputs.temperature}
                    onChange={(e) => setDewpointInputs({...dewpointInputs, temperature: parseFloat(e.target.value)})}
                    style={baseStyles.input}
                    required
                  />
                  <select
                    value={dewpointInputs.temperatureUnit}
                    onChange={(e) => setDewpointInputs({...dewpointInputs, temperatureUnit: e.target.value as any})}
                    style={baseStyles.select}
                  >
                    <option value="fahrenheit">°F</option>
                    <option value="celsius">°C</option>
                    <option value="kelvin">K</option>
                  </select>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t('dewpointTemperature')}</label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    step="0.1"
                    value={dewpointInputs.dewpoint}
                    onChange={(e) => setDewpointInputs({...dewpointInputs, dewpoint: parseFloat(e.target.value)})}
                    style={baseStyles.input}
                    required
                  />
                  <select
                    value={dewpointInputs.dewpointUnit}
                    onChange={(e) => setDewpointInputs({...dewpointInputs, dewpointUnit: e.target.value as any})}
                    style={baseStyles.select}
                  >
                    <option value="fahrenheit">°F</option>
                    <option value="celsius">°C</option>
                    <option value="kelvin">K</option>
                  </select>
                </div>
              </div>

              <button type="submit" style={baseStyles.calculateButton}>
                {t('calculateButton')}
              </button>
            </form>
          )}

          {error && (
            <div style={baseStyles.errorMessage}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsCardStyle}>
              <h3 style={baseStyles.resultsTitle}>{t('resultsTitle')}</h3>

              <div style={baseStyles.heatIndexDisplay}>
                <div style={heatIndexValueStyle}>
                  {results.heatIndex.toFixed(1)}{results.unit}
                </div>
                <div style={baseStyles.heatIndexLabel}>{t('heatIndexLabel')}</div>
              </div>

              <div style={getWarningLevelStyle(results.warningLevel.level)}>
                <h4 style={baseStyles.warningTitle}>{results.warningLevel.title}</h4>
                <p style={baseStyles.warningDesc}>{results.warningLevel.description}</p>
              </div>

              <div style={baseStyles.additionalInfo}>
                <h4 style={baseStyles.additionalInfoTitle}>{t('safetyInfoTitle')}</h4>
                <ul style={baseStyles.additionalInfoList}>
                  <li style={baseStyles.additionalInfoItem}>{t('safetyInfo1')}</li>
                  <li style={baseStyles.additionalInfoItem}>{t('safetyInfo2')}</li>
                  <li style={baseStyles.additionalInfoItem}>{t('safetyInfo3')}</li>
                  <li style={baseStyles.additionalInfoItem}>{t('safetyInfo4')}</li>
                  <li style={baseStyles.additionalInfoItem}>{t('safetyInfo5')}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('howToUseStep1')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep2')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep3')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep4')}</li>
          <li style={baseStyles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <div style={baseStyles.insightCard}>
          <p style={paragraphStyle}>
            <strong style={baseStyles.strong}>{t('insight1Title')}</strong> {t('insight1Text')}
          </p>
        </div>

        <div style={baseStyles.insightCard}>
          <p style={paragraphStyle}>
            <strong style={baseStyles.strong}>{t('insight2Title')}</strong> {t('insight2Text')}
          </p>
        </div>

        <div style={baseStyles.insightCard}>
          <p style={paragraphStyle}>
            <strong style={baseStyles.strong}>{t('insight3Title')}</strong> {t('insight3Text')}
          </p>
        </div>

        <div style={baseStyles.insightCard}>
          <p style={paragraphStyle}>
            <strong style={baseStyles.strong}>{t('insight4Title')}</strong> {t('insight4Text')}
          </p>
        </div>

        <div style={baseStyles.insightCard}>
          <p style={paragraphStyle}>
            <strong style={baseStyles.strong}>{t('insight5Title')}</strong> {t('insight5Text')}
          </p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{...h2Style, fontSize: '1.5rem', marginTop: '1.5rem'}}>{t('detailsFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsFormulaParagraph')}</p>

        <h3 style={{...h2Style, fontSize: '1.5rem', marginTop: '1.5rem'}}>{t('detailsValidityTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsValidityParagraph')}</p>

        <h3 style={{...h2Style, fontSize: '1.5rem', marginTop: '1.5rem'}}>{t('detailsLimitationsTitle')}</h3>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t('detailsLimitation1')}</li>
          <li style={baseStyles.listItem}>{t('detailsLimitation2')}</li>
          <li style={baseStyles.listItem}>{t('detailsLimitation3')}</li>
          <li style={baseStyles.listItem}>{t('detailsLimitation4')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: '1.5rem', marginTop: '1.5rem'}}>{t('detailsAlternativesTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsAlternativesParagraph')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq6Answer')}</p>
        </div>

        <div style={{...baseStyles.faqItem, borderBottom: 'none', paddingBottom: 0}}>
          <h3 style={baseStyles.faqQuestion}>{t('faq7Question')}</h3>
          <p style={baseStyles.faqAnswer}>{t('faq7Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>
            <a
              href="https://www.purdue.edu/engineering/ABE/INPREPared/heat-forecast-tools-explained/"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://en.wikipedia.org/wiki/Heat_index"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://www.onehourheatandair.com/expert-tips/home-life/the-heat-index-how-its-calculated-and-what-it-me/"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://bamwx.com/blog/education/heat-index-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://1degreeoutside.com/tuesday-1outside-insights-explaining-heat-index-highlighting-storms-that-deliver-cooling/"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference5')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://www.calculator.net/heat-index-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference6')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://www.wpc.ncep.noaa.gov/html/heatindex.shtml"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference7')}
            </a>
          </li>
          <li style={baseStyles.listItem}>
            <a
              href="https://www.wdrb.com/weather/wdrb-weather-blog/what-is-the-heat-index-how-is-it-calculated/article_546c2de3-7444-405c-8668-55da62412f5a.html"
              target="_blank"
              rel="noopener noreferrer"
              style={baseStyles.referenceLink}
            >
              {t('reference8')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}